import type { Disposable } from "./lifecycle";


export type EventMap = Record<string, boolean | number | string | Readonly<Record<string, unknown>>>;
export type EventType<T extends EventMap> = keyof T;
export type EventValue<T extends EventMap, E extends EventType<T>> = T[E];
export type EventCallback<T extends EventMap, E extends EventType<T>> = (ev: Readonly<EventValue<T, E>>) => void;


/**
 * Manage a collection of event listeners.
 *
 * Available event types & their values are determined by the `EventMap` generic
 * parameter.
 *
 * @example
 * ```ts
 *   type ExampleEvents = {
 *     foo: number;
 *     bar: { baz: string };
 *   }
 *
 *   const events = new EventManager<ExampleEvents>();
 *
 *   events.on('foo' ev => { console.log('notified:', ev); });
 *   events.on('bar' ev => { console.log('notified:', ev.baz); })
 *
 *   events.notify('foo', 5);                // prints `notified: 5`
 *   events.notify('bar', { baz: 'hello' }); // prints `notified: hello`
 * ```
 */
export class EventManager<T extends EventMap> implements Disposable {
	#paused = false;
	readonly #listeners = new Map<unknown, Set<(ev: any) => void>>(); // eslint-disable-line @typescript-eslint/no-explicit-any

	/**
	 * Add a `listener` for a particular `event`.
	 *
	 * Does nothing if `listener` was already added for that `event`.
	 */
	on<E extends EventType<T>>(event: E, listener: EventCallback<T, E>): void {
		let eventListeners = this.#listeners.get(event);
		if(!eventListeners) {
			eventListeners = new Set();
			this.#listeners.set(event, eventListeners);
		}

		eventListeners.add(listener);
	}

	/**
	 * Remove a `listener` for a particular `event`.
	 *
	 * Does nothing if `listener` was not previously added for that `event`.
	 */
	off<E extends EventType<T>>(event: E, listener: EventCallback<T, E>): void {
		this.#listeners.get(event)?.delete(listener);
	}

	/**
	 * Notify all listeners for a particular event.
	 *
	 * @param event The name of the event ot notify.
	 * @param value The value passed to the event's listeners.
	 * @returns `true` if there were any listeners to be notified for the event,
	 * 	otherwise `false`.
	 */
	notify<E extends EventType<T>>(event: E, value: EventValue<T, E>): boolean {
		if(this.#paused) {
			return false;
		}

		const eventListeners = this.#listeners.get(event);
		if(!eventListeners) {
			return false;
		}


		for(const listener of eventListeners) {
			listener(value);
		}

		return true;
	}

	pause(): void {
		this.#paused = true;
	}

	resume(): void {
		this.#paused = false;
	}

	/**
	 * Removes all listeners from all events.
	 */
	dispose(): void {
		this.#listeners.clear();
	}
}
